// Foundries that split a bill (PLAN-UI §28.4).
//
// A forge used to be one foundry's act: the rook or king named on the command
// paid the whole recipe out of its own hopper, so a pawn's worth of ore in each
// of two adjacent rooks built nothing. That is a stockpile the player can see
// and cannot spend, and the answer nobody likes is shuttling ore between two
// machines that are touching each other.
//
// So the *square* is what a forge is aimed at, and **every foundry of yours
// touching that square splits the bill**. Nothing else about forging moves: one
// piece appears, it costs the turn, the recipe is the same recipe, and a lone
// foundry is simply a coalition of one — which is why the classic path is
// unchanged by construction rather than by a branch.
//
// Its own file because `machines.ts` is already three thousand lines and this
// is a self-contained question ("who pays, and how much each"). The import
// edge back into `machines.ts` is a cycle, and a harmless one: every crossing
// is a *call* made long after both module bodies have run, never a read at
// module init — the same argument the reducer's three-file split is built on.

import { takeStockOf } from "./structures";
import { type Color, fileOf, KING, KING_DIRS, type PieceType, pieceColor, pieceType, rankOf } from "../board";
import {
  pieceAt,
  type Position
} from "../position";
import { type Bill, billFor } from "../materials";
import { type Grade, WAREHOUSE } from "../rules";
import { bufferAtLeast, DRONE_WAREHOUSE, type IndustryState, kingBusyForForge, structureAt, takeUnitOf, unitMeets } from "./state";
import {
  type ForgeError,
  forgeReaches,
  forgeSquares,
  isFoundryAt,
  mintableFor
} from "./machines";

/**
 * Every foundry of `owner` that could pay toward a piece landing on `at`.
 *
 * The eight neighbours, **in ascending square order** — a packed square sorts
 * rank-then-file, which is this codebase's canonical order, so two clients
 * deriving one board from one log divide the same bill the same way.
 *
 * A neighbour joins when it is a foundry of yours that could act *and could
 * put the piece down there itself*: `forgeReaches` is what carries the
 * moat, so a foundry on the far side of a wall
 * cannot reach across it to help pay. Asked last, because most neighbours are
 * not foundries at all and the cheap tests throw them out first.
 *
 * **A king that has already worked this turn is left out silently**, not
 * refused. It gets one job a turn and this is not it; the coalition simply
 * pays without it, and only a *lead* king that is busy is a refusal
 * (`canForgeAt`) — because that one is somebody clicking on it.
 *
 * Nothing here is about the recipe: who touches a square and could act is a
 * fact about the board, which is what lets one sweep answer five bills.
 */
/** Is `b` inside a warehouse on `a`'s supply reach (§33 I5, Chebyshev)? */
export const inWarehouseReach = (a: number, b: number): boolean =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b))) <= WAREHOUSE.reach;

const byDistance = (at: number) => (a: number, b: number) =>
  Math.max(Math.abs(fileOf(a) - fileOf(at)), Math.abs(rankOf(a) - rankOf(at))) -
    Math.max(Math.abs(fileOf(b) - fileOf(at)), Math.abs(rankOf(b) - rankOf(at))) || a - b;

/** `owner`'s warehouses within reach of `at`, nearest first, ties by square. */
export function warehousesFor(industry: IndustryState, owner: Color, at: number): number[] {
  const out: number[] = [];
  for (const [sq, structure] of industry.structures) {
    if (structure.kind === DRONE_WAREHOUSE && structure.owner === owner && inWarehouseReach(sq, at)) out.push(sq);
  }
  return out.sort(byDistance(at));
}

export function fundersFor(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color
): number[] {
  const out: number[] = [];
  for (const offset of KING_DIRS) {
    const sq = at + offset;
    const piece = pieceAt(position, sq);
    if (!piece || pieceColor(piece) !== owner) continue;
    if (!isFoundryAt(position, industry, sq)) continue;
    if (pieceType(piece) === KING && kingBusyForForge(industry, sq)) continue;
    if (!forgeReaches(position, sq, at)) continue;
    out.push(sq);
  }
  return out.sort((a, b) => a - b);
}

/**
 * The foundries that will actually pay for `type` landing on `at`, or null.
 *
 * Every funder, including one holding nothing — it contributes nothing and
 * costs nothing, and a list that is "who is in the coalition" rather than "who
 * happened to have metal" is the list the payment walks in order.
 *
 * Rows are independent because no two rows of a bill take the same unit (one
 * row per ladder, resource rows at distinct grades — `Bill`), so summing a row
 * across funders cannot double-count one.
 */
export function coalitionFor(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color,
  type: PieceType
): number[] | null {
  const funders = fundersFor(position, industry, at, owner);
  if (funders.length === 0) return null;
  return coalitionPays(position, industry, funders, type, warehousesFor(industry, owner, at)) ? funders : null;
}

/**
 * Can these funders cover every row between them?
 *
 * Separate from `coalitionFor` only so `canForgeAt` can tell "nobody touches
 * it" from "they are short" without sweeping the neighbours twice — it is
 * asked from inside `evaluate`.
 */
function coalitionPays(
  position: Position,
  industry: IndustryState,
  funders: readonly number[],
  type: PieceType,
  reserve: readonly number[] = []
): boolean {
  return coalitionPaysBill(industry, funders, billFor(type), reserve);
}

/**
 * The same question with the bill already in hand.
 *
 * `billFor` mints a fresh array of fresh rows every call, and the callers that
 * ask this of eight squares in a row are asking about one recipe — so the bill
 * is built once by them and this takes it.
 */
export function coalitionPaysBill(
  industry: IndustryState,
  funders: readonly number[],
  bill: Bill,
  /** Warehouses that finish what the funders cannot (§33 I5). */
  reserve: readonly number[] = []
): boolean {
  for (const row of bill) {
    let have = 0;
    for (const sq of funders) {
      have += bufferAtLeast(industry, sq, row.grade as Grade, "in", row.line);
      if (have >= row.count) break;
    }
    for (const sq of reserve) {
      if (have >= row.count) break;
      for (const unit of structureAt(industry, sq)!.stock) if (unitMeets(unit, row.line, row.grade)) have++;
    }
    if (have < row.count) return false;
  }
  return true;
}

/**
 * Pay a bill out of a coalition: **equal shares where it divides, the
 * remainder on the lowest squares.**
 *
 * One unit per member per round, round after round, skipping a member with
 * nothing left to give. That is the whole rule, and it falls out of it that
 * two full hoppers split four as 2/2 and five as 3/2, and that a member
 * holding one unit gives its one unit and the rest falls to the others. A
 * proportional split would be the other candidate and is worse: it makes what
 * each machine pays depend on what it happened to be hoarding, which is
 * invisible on the board.
 *
 * *Within* a member, `takeUnitOf` still spends the worst it holds — the
 * standing "take the best, spend the worst" rule is untouched, this only
 * decides which hopper the next unit comes out of.
 *
 * Returns how much each member paid, parallel to `members`, because the caller
 * has to know which kings actually worked.
 */
export function payCoalition(
  industry: IndustryState,
  members: readonly number[],
  bill: Bill,
  /** Warehouses that finish what the members cannot (§33 I5), nearest first. */
  reserve: readonly number[] = []
): number[] {
  const paid = members.map(() => 0);
  for (const row of bill) {
    let left = row.count;
    while (left > 0) {
      let took = 0;
      for (let i = 0; i < members.length && left > 0; i++) {
        if (takeUnitOf(industry, members[i], row.line, row.grade) < 0) continue;
        paid[i]++;
        left--;
        took++;
      }
      // The members are dry: a warehouse in reach finishes the row (§33 I5).
      if (took === 0 && reserve.some((sq) => takeStockOf(structureAt(industry, sq)!, row.line, row.grade))) {
        left--;
        continue;
      }
      // A round that took nothing cannot be followed by one that does. The
      // caller asked `coalitionFor` first or it did not; this is the second
      // case, and half a bill spent on nothing is worse than a throw.
      if (took === 0) throw new Error("this coalition cannot pay that bill");
    }
  }
  return paid;
}

/**
 * The refusals that are about the *foundry* rather than about the square.
 *
 * Split out because they cannot differ between one of a foundry's squares and
 * another, so `fundableSquares` asks them once rather than eight times — and
 * because the same three questions are what `canForge` has always asked of a
 * lead.
 */
function leadRefusal(
  position: Position,
  industry: IndustryState,
  lead: number,
  owner: Color
): ForgeError | null {
  const piece = pieceAt(position, lead);
  if (!piece || pieceColor(piece) !== owner || !isFoundryAt(position, industry, lead)) {
    return "not-your-foundry";
  }
  // A *lead* king that has already worked is a refusal rather than a silent
  // exclusion: somebody clicked on it, and a click that cannot mean anything
  // says why.
  if (pieceType(piece) === KING && kingBusyForForge(industry, lead)) return "king-busy";
  return null;
}

/**
 * Can a piece of `type` be forged onto `at`, and why not.
 *
 * The square-shaped question, which is the one the hotbar and the reducer both
 * ask. `lead` is the foundry the player named, when they named one: it adds
 * the refusals that are about *that* machine (it is yours, it is a foundry, it
 * is not a king that has already worked, and the square is one of its own),
 * which a coalition answer would otherwise swallow.
 */
export function canForgeAt(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color,
  type: PieceType,
  lead?: number
): ForgeError | null {
  if (!mintableFor(position, owner).includes(type)) return "not-mintable";

  if (lead !== undefined) {
    const why = leadRefusal(position, industry, lead, owner);
    if (why) return why;
    if (!forgeReaches(position, lead, at)) return "no-room";
  }

  const funders = fundersFor(position, industry, at, owner);
  if (funders.length === 0) return "no-foundry";
  if (!coalitionPays(position, industry, funders, type, warehousesFor(industry, owner, at))) return "no-coalition";
  return null;
}

/**
 * The squares this foundry could lead a forge onto and have it paid for.
 *
 * `forgeSquares` says where a piece *fits*; this says where one can also be
 * *afforded*, which since the bill is split is a different list for every
 * square. It is what the board lights and what the planner picks from — one
 * definition, so the opponent cannot propose a square the reducer refuses and
 * the interface cannot light one.
 *
 * `forgeSquares`' own order, which is fixed.
 */
export function fundableSquares(
  position: Position,
  industry: IndustryState,
  foundry: number,
  owner: Color,
  type: PieceType
): number[] {
  // `canForgeAt` per square would be the honest one-liner and it is asked from
  // inside `evaluate`, thousands of times a turn: everything above the
  // coalition is a fact about the *foundry* and does not change from one of
  // its own squares to the next, so it is asked once. Measured — the naive
  // shape put the planner's cost test over its budget on a grown board.
  if (!mintableFor(position, owner).includes(type)) return [];
  if (leadRefusal(position, industry, foundry, owner) !== null) return [];
  const bill = billFor(type);
  return fundingOptions(position, industry, foundry, owner)
    .filter((option) => coalitionPaysBill(industry, option.funders, bill, option.reserve))
    .map((option) => option.at);
}

/** A square a forge could land on, and everybody who would chip in for it. */
export interface FundingOption {
  at: number;
  funders: number[];
  /** Warehouses in reach that finish a short bill (§33 I5). */
  reserve: number[];
}

/**
 * Every square this foundry could lead a forge onto, with who would pay.
 *
 * The sweep behind `fundableSquares`, exposed because **`evaluate` asks it of
 * one foundry about five recipes in a row** and neither the squares nor the
 * funders depend on the recipe — only the bill does. Repeating the sweep per
 * recipe was measured at a third of the planner's whole turn on a grown board,
 * which is the one thing in this game with a deadline attached.
 *
 * Squares with nobody to pay for them are dropped, so an option always has at
 * least one funder and the caller only has to weigh a bill against it.
 */
export function fundingOptions(
  position: Position,
  industry: IndustryState,
  foundry: number,
  owner: Color
): FundingOption[] {
  if (leadRefusal(position, industry, foundry, owner) !== null) return [];
  const out: FundingOption[] = [];
  for (const at of forgeSquares(position, foundry)) {
    const funders = fundersFor(position, industry, at, owner);
    if (funders.length > 0) out.push({ at, funders, reserve: warehousesFor(industry, owner, at) });
  }
  return out;
}
